"use client";

import React, { useRef, useState, useCallback } from "react";
import type { BlockProps } from "./blocks/registry";
import { Heading } from "../design-system/atoms/Heading";
import AideFinanciere from "../design-system/molecules/AideFinanciere";

type StrapiMedia = {
  url?: string;
};

type CtaData = {
  text?: string;
  link?: string;
};

type AideItem = {
  id?: number | string;
  titre?: string;
  descr?: string;
  icon?: StrapiMedia;
  iconHover?: StrapiMedia;
  cta?: CtaData;
};

type AideBlockData = {
  id?: number | string;
  documentId?: string;
  __component?: string;
  title?: string;
  aide?: AideItem[];
  [key: string]: unknown;
};

type MappedAideData = {
  title: string;
  text: string;
  iconSrc: string;
  image: string;
  cta: {
    text: string;
    link: string;
  };
};

export function mapAideData(strapiData: AideBlockData): MappedAideData[] {
  if (!strapiData || !Array.isArray(strapiData.aide)) return [];

  return strapiData.aide.map((item) => ({
    title: item.titre || "",
    text: item.descr || "",
    iconSrc: item.icon?.url || "/fallback-icon.svg",
    image: item.iconHover?.url || "/fallback-image.jpg",
    cta: {
      text: item.cta?.text || "En savoir plus",
      link: item.cta?.link || "#",
    },
  }));

}

export default function Aide({ data }: BlockProps<AideBlockData>) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  const aides = data ? mapAideData(data) : [];

  const goTo = useCallback((index: number) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollTo({ left: index * el.clientWidth, behavior: "smooth" });
  }, []);

  const handleScroll = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setActive(Math.round(el.scrollLeft / el.clientWidth));
  }, []);

  if (!data) return null;

  const title = data.title || "";
  const titleWords = title.split(" ");
  const titleStart = titleWords.slice(0, -1).join(" ");
  const titleEnd = titleWords.slice(-1).join(" ");

  const isFirst = active === 0;
  const isLast = active === aides.length - 1;

  const desktopGridClass =
    aides.length === 1
      ? "lg:grid-cols-1 lg:max-w-[520px]"
      : aides.length === 2
        ? "lg:grid-cols-2 lg:max-w-[1100px]"
        : "lg:grid-cols-3";

  return (
    <section
      className="mx-auto pt-5 px-5 pb-5 md:pb-32 bg-muted flex items-center justify-center overflow-hidden"
      key={data.documentId}
    >
      <div className="lms-container w-full min-w-0">
        <Heading
          as="h2"
          className="text-4xl lg:text-[44px] font-bold text-text text-center "
        >
          {titleStart} <span className="text-secondary">{titleEnd}</span>
        </Heading>

        {/* Mobile : slider (1 carte) | Desktop : grille 3 colonnes */}
        <div
          ref={trackRef}
          onScroll={handleScroll}
          className={`flex overflow-x-auto snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
            lg:grid lg:w-full lg:mx-auto lg:gap-10 lg:overflow-visible
            ${desktopGridClass} `}
        >
          {aides.map((aide, i) => (
            <div
              key={i}
              className="w-full shrink-0 snap-center px-1 lg:w-auto lg:shrink lg:px-0"
            >
              <AideFinanciere
                image={aide.image}
                iconSrc={aide.iconSrc}
                title={aide.title}
                text={aide.text}
                ctaLabel={aide.cta.text}
                ctaLink={aide.cta.link}
              />
            </div>
          ))}
        </div>

        {/* Contrôles : flèches + dots (mobile uniquement) */}
        {aides.length > 1 && (
          <div className="mt-6 flex items-center justify-center gap-4 lg:hidden">
            <button
              type="button"
              aria-label="Aide précédente"
              onClick={() => goTo(active - 1)}
              disabled={isFirst}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-primaryGreen text-white transition disabled:opacity-30"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>

            <div className="flex items-center gap-2">
              {aides.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Aller à l'aide ${i + 1}`}
                  aria-current={i === active}
                  onClick={() => goTo(i)}
                  className={`h-2 rounded-full transition-all duration-300 ${i === active ? "w-6 bg-secondary" : "w-2 bg-gray-400"
                    }`}
                />
              ))}
            </div>

            <button
              type="button"
              aria-label="Aide suivante"
              onClick={() => goTo(active + 1)}
              disabled={isLast}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-primaryGreen text-white transition disabled:opacity-30"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}